/**
 * Graph view — author-only, mock data, spatial layout with focus mode.
 */

import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogContent,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import {
  Background,
  Controls,
  Handle,
  MarkerType,
  MiniMap,
  Position,
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'

import {
  EDGE_TYPE_LABEL,
  EdgeType,
  GraphNode,
  NODE_TYPE_LABEL,
  NodeType,
  SECTION_STAGE,
  SOURCE_BUCKET_LABEL,
  STAGE_LABEL,
  graphData,
} from './graph/mockGraph'

// --- Visual tokens ---------------------------------------------------------

const NODE_TYPE_COLOR: Record<NodeType, string> = {
  Section: '#7C9A92',
  Person: '#C98F7C',
  Place: '#9BAB8E',
  Concept: '#A88FB5',
  Motif: '#D4A574',
  Moment: '#B8836F',
  Source: '#8FA4B5',
  Arc: '#7C8FA8',
  SourceFile: '#6E7F8C',
}

const EDGE_TYPE_COLOR: Record<EdgeType, string> = {
  compresses_into: '#3A3A3A',
  echoes: '#7C9A92',
  mentions: '#999999',
  in_arc: '#A88FB5',
  in_source: '#6E7F8C',
}

const EDGE_TYPE_ORDER: EdgeType[] = ['compresses_into', 'echoes', 'mentions', 'in_arc', 'in_source']

// --- Layout (deterministic, cluster by type) -------------------------------

interface TypeCenter {
  x: number
  y: number
  radius: number
}

const TYPE_CENTERS: Record<NodeType, TypeCenter> = {
  Section: { x: 0, y: 0, radius: 320 },
  Motif: { x: 0, y: -560, radius: 130 },
  Concept: { x: -700, y: -260, radius: 220 },
  Moment: { x: 700, y: -260, radius: 320 },
  Person: { x: -700, y: 300, radius: 260 },
  Place: { x: 700, y: 300, radius: 150 },
  Source: { x: -360, y: 620, radius: 150 },
  Arc: { x: 360, y: 620, radius: 90 },
  SourceFile: { x: 0, y: 900, radius: 320 },
}

function computeLayout(nodes: GraphNode[]): Map<string, { x: number; y: number }> {
  const byType = new Map<NodeType, GraphNode[]>()
  for (const node of nodes) {
    if (!byType.has(node.type)) {
      byType.set(node.type, [])
    }
    byType.get(node.type)!.push(node)
  }

  const positions = new Map<string, { x: number; y: number }>()
  for (const [type, typeNodes] of byType.entries()) {
    const center = TYPE_CENTERS[type]
    // Sort alphabetically for stable positions
    const sorted = [...typeNodes].sort((a, b) => a.id.localeCompare(b.id))
    sorted.forEach((node, index) => {
      const angle = (index / sorted.length) * 2 * Math.PI - Math.PI / 2
      positions.set(node.id, {
        x: center.x + center.radius * Math.cos(angle),
        y: center.y + center.radius * Math.sin(angle),
      })
    })
  }
  return positions
}

const POSITIONS = computeLayout(graphData.nodes)

// --- Custom node component -------------------------------------------------

interface CustomNodeData extends GraphNode {
  isFocus: boolean
  isNeighbor: boolean
}

const CustomNode = memo(({ data }: { data: CustomNodeData }) => {
  const opacity = data.isFocus || data.isNeighbor ? 1 : 0.22
  const isFocus = data.isFocus
  return (
    <Box
      sx={{
        opacity,
        bgcolor: NODE_TYPE_COLOR[data.type],
        color: 'white',
        padding: isFocus ? '8px 12px' : '5px 9px',
        borderRadius: 1.5,
        border: isFocus ? '3px solid #1a1a1a' : '1px solid rgba(0,0,0,0.15)',
        minWidth: 60,
        maxWidth: 220,
        fontSize: isFocus ? 13 : 11,
        fontWeight: isFocus ? 700 : 500,
        textAlign: 'center',
        direction: 'rtl',
        cursor: 'pointer',
        boxShadow: isFocus ? '0 4px 12px rgba(0,0,0,0.2)' : '0 1px 3px rgba(0,0,0,0.1)',
        transition: 'all 0.2s',
        userSelect: 'none',
        lineHeight: 1.3,
      }}
    >
      <Handle type="target" position={Position.Top} style={{ opacity: 0, pointerEvents: 'none' }} />
      <Handle type="source" position={Position.Bottom} style={{ opacity: 0, pointerEvents: 'none' }} />
      {data.label}
    </Box>
  )
})

CustomNode.displayName = 'CustomNode'

const nodeTypes = { custom: CustomNode }

// --- Main view -------------------------------------------------------------

function GraphView() {
  const [focusId, setFocusId] = useState<string>('motif:button')
  const [history, setHistory] = useState<string[]>([])
  const [searchOpen, setSearchOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const { setCenter, getZoom } = useReactFlow()

  useEffect(() => {
    const onKeydown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        setSearchOpen((prev) => !prev)
      } else if (event.key === 'Escape' && searchOpen) {
        setSearchOpen(false)
      }
    }
    window.addEventListener('keydown', onKeydown)
    return () => window.removeEventListener('keydown', onKeydown)
  }, [searchOpen])

  const filteredNodes = useMemo(() => {
    const query = searchQuery.trim()
    if (!query) {
      return graphData.nodes.slice(0, 80)
    }
    const lower = query.toLowerCase()
    return graphData.nodes
      .filter((node) => node.label.toLowerCase().includes(lower) || NODE_TYPE_LABEL[node.type].includes(query))
      .slice(0, 80)
  }, [searchQuery])

  const goToNode = useCallback(
    (id: string) => {
      if (id !== focusId) {
        setHistory((prev) => [...prev, focusId])
        setFocusId(id)
      }
    },
    [focusId],
  )

  const handleSearchSelect = useCallback(
    (id: string) => {
      goToNode(id)
      setSearchOpen(false)
      setSearchQuery('')
    },
    [goToNode],
  )

  const neighborIds = useMemo(() => {
    const set = new Set<string>([focusId])
    for (const edge of graphData.edges) {
      if (edge.sourceId === focusId) {
        set.add(edge.targetId)
      }
      if (edge.targetId === focusId) {
        set.add(edge.sourceId)
      }
    }
    return set
  }, [focusId])

  const flowNodes = useMemo(() => {
    return graphData.nodes.map((node) => ({
      id: node.id,
      position: POSITIONS.get(node.id) ?? { x: 0, y: 0 },
      type: 'custom',
      data: {
        ...node,
        isFocus: node.id === focusId,
        isNeighbor: neighborIds.has(node.id),
      },
    }))
  }, [focusId, neighborIds])

  const flowEdges = useMemo(() => {
    return graphData.edges.map((edge) => {
      const involves = edge.sourceId === focusId || edge.targetId === focusId
      const color = EDGE_TYPE_COLOR[edge.type]
      return {
        id: edge.id,
        source: edge.sourceId,
        target: edge.targetId,
        animated: involves && edge.type === 'compresses_into',
        style: {
          stroke: color,
          strokeWidth: involves ? (edge.type === 'compresses_into' ? 2.5 : 2) : 1,
          opacity: involves ? 0.95 : 0.1,
          strokeDasharray: edge.type === 'echoes' ? '5 4' : undefined,
        },
        markerEnd: edge.directional ? { type: MarkerType.ArrowClosed, color, width: 18, height: 18 } : undefined,
      }
    })
  }, [focusId])

  // Pan to focused node when it changes (smooth)
  useEffect(() => {
    const pos = POSITIONS.get(focusId)
    if (pos) {
      setCenter(pos.x, pos.y, { zoom: getZoom(), duration: 600 })
    }
  }, [focusId, setCenter, getZoom])

  const handleNodeClick = useCallback(
    (_: React.MouseEvent, node: { id: string }) => {
      goToNode(node.id)
    },
    [goToNode],
  )

  const handleSelectFromPanel = useCallback(
    (id: string) => {
      goToNode(id)
    },
    [goToNode],
  )

  const handleBack = () => {
    if (history.length === 0) {
      return
    }
    const prev = history[history.length - 1]
    setHistory((h) => h.slice(0, -1))
    setFocusId(prev)
  }

  const focus = graphData.nodes.find((n) => n.id === focusId)
  const focusEdges = useMemo(
    () => graphData.edges.filter((e) => e.sourceId === focusId || e.targetId === focusId),
    [focusId],
  )

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)' }}>
      <Box sx={{ flex: 1, position: 'relative' }}>
        <ReactFlow
          nodes={flowNodes}
          edges={flowEdges}
          nodeTypes={nodeTypes}
          onNodeClick={handleNodeClick}
          fitView
          fitViewOptions={{ padding: 0.15, maxZoom: 0.8 }}
          minZoom={0.2}
          maxZoom={2.5}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable
          proOptions={{ hideAttribution: true }}
        >
          <Background gap={28} color="#e0dcd2" />
          <Controls showInteractive={false} />
          <MiniMap
            pannable
            zoomable
            nodeColor={(n) => NODE_TYPE_COLOR[(n.data as unknown as CustomNodeData).type]}
            nodeStrokeWidth={2}
            maskColor="rgba(245,242,235,0.7)"
          />
        </ReactFlow>
      </Box>

      <Box
        sx={{
          width: 360,
          borderInlineStart: 1,
          borderColor: 'divider',
          overflow: 'auto',
          bgcolor: 'background.paper',
          direction: 'rtl',
        }}
      >
        <Stack sx={{ p: 2 }} spacing={2}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Button size="small" disabled={history.length === 0} onClick={handleBack}>
              ← חזרה
            </Button>
            <Button size="small" onClick={() => setSearchOpen(true)}>
              🔍 חיפוש (⌘K)
            </Button>
            <Typography variant="caption" color="text.secondary">
              {history.length} ברצף
            </Typography>
          </Stack>

          {focus && (
            <Box>
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap', gap: 0.5 }}>
                <Chip
                  label={NODE_TYPE_LABEL[focus.type]}
                  size="small"
                  sx={{ bgcolor: NODE_TYPE_COLOR[focus.type], color: 'white' }}
                />
                {focus.type === 'Section' && SECTION_STAGE[focus.id] && (
                  <Chip label={STAGE_LABEL[SECTION_STAGE[focus.id]]} size="small" variant="outlined" />
                )}
                {focus.type === 'SourceFile' && focus.attrs?.bucket && (
                  <Chip
                    label={SOURCE_BUCKET_LABEL[focus.attrs.bucket as keyof typeof SOURCE_BUCKET_LABEL]}
                    size="small"
                    variant="outlined"
                  />
                )}
              </Stack>
              <Typography variant="h6" sx={{ fontFamily: 'serif' }}>
                {focus.label}
              </Typography>
              {focus.attrs && (
                <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
                  {Object.entries(focus.attrs)
                    .filter(([key]) => key !== 'bucket')
                    .map(([key, value]) => (
                      <Chip key={key} label={`${key}: ${value}`} size="small" variant="outlined" />
                    ))}
                </Stack>
              )}
            </Box>
          )}

          {EDGE_TYPE_ORDER.map((type) => {
            const group = focusEdges.filter((e) => e.type === type)
            if (group.length === 0) {
              return null
            }
            return (
              <Box key={type}>
                <Typography variant="overline" sx={{ display: 'block', mb: 0.5, color: EDGE_TYPE_COLOR[type] }}>
                  {EDGE_TYPE_LABEL[type]} ({group.length})
                </Typography>
                <Stack spacing={0.5}>
                  {group.map((edge) => {
                    const otherId = edge.sourceId === focusId ? edge.targetId : edge.sourceId
                    const other = graphData.nodes.find((n) => n.id === otherId)
                    if (!other) {
                      return null
                    }
                    return (
                      <Card
                        key={edge.id}
                        variant="outlined"
                        sx={{ cursor: 'pointer', '&:hover': { borderColor: NODE_TYPE_COLOR[other.type] } }}
                        onClick={() => handleSelectFromPanel(otherId)}
                      >
                        <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Chip
                              label={NODE_TYPE_LABEL[other.type]}
                              size="small"
                              sx={{
                                bgcolor: NODE_TYPE_COLOR[other.type],
                                color: 'white',
                                fontSize: 10,
                                height: 18,
                              }}
                            />
                            <Typography variant="body2" sx={{ flexGrow: 1 }}>
                              {other.label}
                            </Typography>
                            {edge.status === 'proposed' && (
                              <Chip
                                label="הצעה"
                                size="small"
                                color="warning"
                                variant="outlined"
                                sx={{ fontSize: 10, height: 18 }}
                              />
                            )}
                          </Stack>
                          {edge.gloss && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block', mt: 0.5, lineHeight: 1.5 }}
                            >
                              {edge.gloss}
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    )
                  })}
                </Stack>
              </Box>
            )
          })}
        </Stack>
      </Box>

      <Dialog
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { direction: 'rtl' } }}
      >
        <DialogContent sx={{ p: 2 }}>
          <TextField
            autoFocus
            fullWidth
            placeholder="חפש צומת... (לפי שם או סוג)"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && filteredNodes.length > 0) {
                handleSearchSelect(filteredNodes[0].id)
              }
            }}
            sx={{ mb: 2 }}
            size="small"
          />
          <List dense sx={{ maxHeight: 420, overflow: 'auto', py: 0 }}>
            {filteredNodes.length === 0 ? (
              <Typography sx={{ p: 2, color: 'text.secondary' }} variant="body2">
                אין תוצאות
              </Typography>
            ) : (
              filteredNodes.map((node) => (
                <ListItemButton key={node.id} onClick={() => handleSearchSelect(node.id)} sx={{ py: 0.75 }}>
                  <Chip
                    label={NODE_TYPE_LABEL[node.type]}
                    size="small"
                    sx={{
                      bgcolor: NODE_TYPE_COLOR[node.type],
                      color: 'white',
                      fontSize: 10,
                      height: 18,
                      marginInlineEnd: 1,
                      minWidth: 50,
                    }}
                  />
                  <ListItemText primary={node.label} primaryTypographyProps={{ variant: 'body2' }} />
                </ListItemButton>
              ))
            )}
          </List>
        </DialogContent>
      </Dialog>
    </Box>
  )
}

export default function Graph() {
  return (
    <ReactFlowProvider>
      <GraphView />
    </ReactFlowProvider>
  )
}
